/**
 * Hooks de formatage payroll qui remplacent les hardcodes.
 *
 * Ces hooks utilisent la configuration dynamique par pays
 * pour remplacer les valeurs TND/fr-TN/etc. hardcodées.
 */

import { usePayrollUIConfig } from "./usePayrollUIConfig";

/**
 * Hook pour formatter les montants currency de façon dynamique.
 * Remplace les Intl.NumberFormat('fr-TN', {currency: 'TND'}) hardcodés.
 */
export const useCurrencyFormatter = () => {
  const { config, isReady } = usePayrollUIConfig();

  return (amount) => {
    if (!isReady || !config) {
      return `${amount || 0}`;
    }

    try {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch (error) {
      console.warn("Erreur formatage currency:", error);
      // Fallback si locale pas supportée
      return `${(amount || 0).toLocaleString()} ${config.currency}`;
    }
  };
};

/**
 * Hook pour récupérer les ranges salaires dynamiques.
 * Remplace les ranges hardcodées (< 1000 DT, etc.) dans PayrollReports.
 */
export const useSalaryRanges = () => {
  const { config, isReady } = usePayrollUIConfig();

  if (!isReady || !config?.salaryRanges) {
    // Fallback pendant loading
    return [
      { label: "< 1000 DT", min: 0, max: 1000 },
      { label: "> 1000 DT", min: 1000, max: Infinity }
    ];
  }

  return config.salaryRanges;
};

/**
 * Hook pour récupérer les defaults working time.
 * Remplace les 173.33h et 21.67 jours hardcodés dans PayrollSettings.
 */
export const useWorkingTimeDefaults = () => {
  const { config, isReady } = usePayrollUIConfig();

  if (!isReady || !config?.workingTime) {
    // Fallback pendant loading
    return {
      hoursPerMonth: 173.33,
      daysPerMonth: 21.67
    };
  }

  return config.workingTime;
};

/**
 * Hook pour récupérer les multipliers overtime.
 * Remplace les 1.25, 1.50, etc. hardcodés dans PayrollSettings.
 */
export const useOvertimeMultipliers = () => {
  const { config, isReady } = usePayrollUIConfig();

  if (!isReady || !config?.overtimeMultipliers) {
    // Fallback pendant loading
    return {
      normal: 1.25,
      night: 1.50,
      weekend: 2.00,
      holiday: 2.50
    };
  }

  return config.overtimeMultipliers;
};

/**
 * Hook pour récupérer les policies de déduction.
 * Remplace les policies hardcodées dans PayrollSettings.
 */
export const useDeductionDefaults = () => {
  const { config, isReady } = usePayrollUIConfig();

  if (!isReady || !config?.deductionPolicies) {
    // Fallback pendant loading
    return {
      absence: "proportional_hourly",
      absenceRate: 1.0,
      lateness: "after_tolerance",
      latenessRate: 0.02,
      maxLatenessPercent: 10.0
    };
  }

  return config.deductionPolicies;
};
